// Single source of truth for the marketing-site pricing promotion.
// All promo-related strings shown to visitors must derive from this file
// (see components/Pricing.jsx). If we change the label, the mechanic, or the
// discount, we edit here and nowhere else.
//
// NOTE: this is deliberately separate from the waitlist "Founding 50" CONTEST
// rewards (see /app/backend/waitlist_game/services/reward_grant_service.py
// and pages/PreLaunchPage.jsx). The contest and this marketing promo happen
// to share the same 50% discount amount by design, but they are independent
// concepts and must not be conflated in copy.

export const pricingPromo = {
  // Human-facing label for the promotion.
  promoLabel: 'Founding Member rate',

  // Mechanic that gates the promotion. Supports two shapes:
  //   { type: 'memberCap', cap: <int> }  -> "first N accounts"
  //   { type: 'endDate',   date: 'YYYY-MM-DD' } -> "ends <date>"
  //
  // The cap is 500 (Ryan confirmed it on 11 Oct 2026).
  promoMechanic: { type: 'memberCap', cap: 500 },

  // Discount applied to the list rate for the promo duration.
  discountPercent: 50,

  // Duration the discount applies for.
  discountDuration: 'first year',
};

/**
 * Render the mechanic clause shown after the discounted price, e.g.
 *   "first 500 accounts"  (memberCap)
 *   "ends Jun 30, 2026"   (endDate)
 * Returns an empty string for unknown shapes so callers don't need to guard.
 */
export const getPromoMechanicText = (mechanic = pricingPromo.promoMechanic) => {
  if (!mechanic || typeof mechanic !== 'object') return '';
  if (mechanic.type === 'memberCap' && Number.isFinite(mechanic.cap)) {
    return `first ${mechanic.cap} accounts`;
  }
  if (mechanic.type === 'endDate' && typeof mechanic.date === 'string') {
    // Human-friendly format; falls back to raw string if Date parsing fails.
    const d = new Date(mechanic.date);
    if (Number.isNaN(d.getTime())) return `ends ${mechanic.date}`;
    return `ends ${d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }
  return '';
};

/**
 * Given a monthly list price and the current billing cadence, return the
 * per-month LIST rate to show as the primary big number on the pricing card.
 *
 *   - Annual toggle: annual list (= listMonthly * 10, i.e. "2 months free")
 *     divided by 12 and rounded to the nearest whole dollar. This is the
 *     effective monthly cost when a visitor picks annual billing at list.
 *   - Monthly toggle: the flat monthly list rate.
 */
export const computeDisplayedListMonthly = (listMonthly, { isAnnual }) => {
  if (isAnnual) {
    const annualList = listMonthly * 10; // 2 months free
    return Math.round(annualList / 12);
  }
  return listMonthly;
};

/**
 * Founding-Member per-month rate = discountPercent off the DISPLAYED list rate
 * (so the strike-through and the discounted number always share the same
 * cadence). Rounded to two decimals.
 */
export const computeDiscountedMonthly = (listMonthly, { isAnnual }) => {
  const displayedList = computeDisplayedListMonthly(listMonthly, { isAnnual });
  const discounted = displayedList * (1 - pricingPromo.discountPercent / 100);
  return Math.round(discounted * 100) / 100;
};

/**
 * SEAM (TODO: memberCap live count) — when we wire the marketing site to a
 * real remaining-slots feed, this is the single place that flips to dynamic
 * copy. Until then it returns null so callers render the static mechanic.
 *
 * A future implementation could shape the return as:
 *   { remaining: number, cap: number }
 * Callers can then choose to render "128 of 500 spots left" etc.
 */
export const getLiveRemainingSpots = () => null;
